import { useState, useRef, useEffect, useMemo } from "react";
import { Play, Pause } from "lucide-react";

interface AudioPlayerProps {
  audioUrl: string;
  className?: string;
}

const AUDIO_PLAYER_PLAY_EVENT = "audio-player:play";
const MAX_WAVEFORM_BARS = 40;

const AudioPlayer = ({ audioUrl, className = "" }: AudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [barCount, setBarCount] = useState(8);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const waveformPool = useMemo(
    () => Array.from({ length: MAX_WAVEFORM_BARS }, () => Math.random() * 0.8 + 0.2),
    []
  );

  useEffect(() => {
    const element = trackRef.current;
    if (!element) return;

    const fitBars = () => {
      const width = element.clientWidth;
      const next = width < 8 ? 0 : Math.max(4, Math.min(MAX_WAVEFORM_BARS, Math.floor(width / 4)));
      setBarCount((current) => (current === next ? current : next));
    };

    fitBars();
    const observer = new ResizeObserver(fitBars);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      audio.src = "";
    };
  }, [audioUrl]);

  useEffect(() => {
    const handleOtherAudioPlay = (event: Event) => {
      const customEvent = event as CustomEvent<HTMLAudioElement>;
      const currentlyPlayingAudio = customEvent.detail;
      const thisAudio = audioRef.current;

      if (!thisAudio || currentlyPlayingAudio === thisAudio) return;
      thisAudio.pause();
      thisAudio.currentTime = 0;
    };

    window.addEventListener(AUDIO_PLAYER_PLAY_EVENT, handleOtherAudioPlay);
    return () => {
      window.removeEventListener(AUDIO_PLAYER_PLAY_EVENT, handleOtherAudioPlay);
    };
  }, []);

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      window.dispatchEvent(
        new CustomEvent<HTMLAudioElement>(AUDIO_PLAYER_PLAY_EVENT, {
          detail: audioRef.current,
        })
      );
      void audioRef.current.play();
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  const waveformData = waveformPool.slice(0, barCount);

  return (
    <div
      className={`flex w-full min-w-0 max-w-full items-center gap-2 ${className}`}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        onClick={togglePlayPause}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black text-white transition-colors hover:bg-gray-800"
      >
        {isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="ml-0.5 h-4 w-4" />
        )}
      </button>

      <div
        ref={trackRef}
        className="relative flex h-8 min-w-0 flex-1 cursor-pointer items-end gap-px overflow-hidden"
        onClick={handleSeek}
      >
        {waveformData.map((height, index) => {
          const barProgress = waveformData.length > 1 ? (index / (waveformData.length - 1)) * 100 : 0;
          const isPastProgress = barProgress <= progressPercentage;

          return (
            <div
              key={index}
              className="min-w-0 flex-1 rounded-sm"
              style={{
                height: `${height * 100}%`,
                backgroundColor: isPastProgress ? "#000" : "#D1D5DB",
                minHeight: "4px",
              }}
            />
          );
        })}

        <div
          className="pointer-events-none absolute bottom-0 top-0 z-10 w-0.5 bg-black"
          style={{ left: `clamp(0px, ${progressPercentage}%, calc(100% - 2px))` }}
        />
      </div>

      <div className="shrink-0 whitespace-nowrap text-right text-xs tabular-nums text-gray-600">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>
    </div>
  );
};

export default AudioPlayer;
